import { Eye, ArrowUpDown, Trash2, Package, ClipboardList, History } from "lucide-react"
import type { TableAction, BulkAction } from "@shared/types/data-table"
import type { PosInventoryItemWithStatus } from "@shared/data/mockPosInventory"
import { createBaseActions, mergeActions, type BaseActionHandlers } from "./base-actions"

interface PosInventorySpecificHandlers {
  onPreview?: (item: PosInventoryItemWithStatus) => void
  onAdjustStock?: (item: PosInventoryItemWithStatus) => void
  onLogWaste?: (item: PosInventoryItemWithStatus) => void
  onCreatePO?: (item: PosInventoryItemWithStatus) => void
  onStockCount?: (item: PosInventoryItemWithStatus) => void
  onViewMovements?: (item: PosInventoryItemWithStatus) => void
}

export type PosInventoryActionHandlers = BaseActionHandlers<PosInventoryItemWithStatus> & PosInventorySpecificHandlers

const createPosInventoryBulkActions = (): BulkAction[] => [
  {
    key: "generate-po",
    label: "Generate PO",
    onClick: (_ids: string[]) => {},
  },
  {
    key: "export",
    label: "Export CSV",
    onClick: (_ids: string[]) => {},
  },
]

const createPosInventoryRowActions = (handlers: PosInventoryActionHandlers): TableAction<PosInventoryItemWithStatus>[] => [
  {
    key: "preview",
    label: "Preview",
    icon: <Eye className="h-4 w-4" />,
    onClick: (item) => handlers.onPreview?.(item),
  },
  {
    key: "adjust-stock",
    label: "Adjust stock",
    icon: <ArrowUpDown className="h-4 w-4" />,
    onClick: (item) => handlers.onAdjustStock?.(item),
  },
  {
    key: "log-waste",
    label: "Log waste",
    icon: <Trash2 className="h-4 w-4" />,
    onClick: (item) => handlers.onLogWaste?.(item),
    condition: (item) => item.currentStock > 0,
  },
  {
    key: "create-po",
    label: "Create purchase order",
    icon: <Package className="h-4 w-4" />,
    onClick: (item) => handlers.onCreatePO?.(item),
    condition: (item) => item.status === "critical" || item.status === "low",
  },
  {
    key: "stock-count",
    label: "Start stock count",
    icon: <ClipboardList className="h-4 w-4" />,
    onClick: (item) => handlers.onStockCount?.(item),
  },
  {
    key: "view-movements",
    label: "View movements",
    icon: <History className="h-4 w-4" />,
    onClick: (item) => handlers.onViewMovements?.(item),
  },
]

export const getPosInventoryActions = (handlers: PosInventoryActionHandlers = {}) => {
  const baseActions = createBaseActions('Inventory Item', handlers, {
    showEdit: true,
    showDelete: false,
    showFlag: false,
    showAddNote: true,
    showCopyId: true,
    showExport: true,
  })

  const specificActions = {
    bulkActions: createPosInventoryBulkActions(),
    rowActions: createPosInventoryRowActions(handlers),
  }

  return mergeActions(baseActions, specificActions)
}

export const getPosInventoryBulkActions = (handlers: PosInventoryActionHandlers = {}): BulkAction[] =>
  getPosInventoryActions(handlers).bulkActions

export const getPosInventoryRowActions = (handlers: PosInventoryActionHandlers = {}): TableAction<PosInventoryItemWithStatus>[] =>
  getPosInventoryActions(handlers).rowActions

export const getPosInventoryHoverActions = (handlers: PosInventoryActionHandlers = {}): TableAction<PosInventoryItemWithStatus>[] => [
  {
    key: "quick-adjust",
    label: "Quick Adjust",
    onClick: (item) => handlers.onAdjustStock?.(item),
    condition: (item) => item.status === "critical" || item.status === "low",
  },
  {
    key: "reorder",
    label: "Reorder",
    onClick: (item) => handlers.onCreatePO?.(item),
    condition: (item) => item.currentStock <= item.reorderPoint,
  },
]
